import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AssetColorLabel, AssetDto, AssetStatus, GalleryDto } from '@nusign/picflow-backend-types';
import { distinctUntilKeyChanged, filter, map, switchMap } from 'rxjs';
import { allFeedbackOptions } from '../../../consts';
import { AssetStatusColorPipe } from '../../../gallery/pipes/asset-status-color.pipe';
import { AssetStatusIconPipe } from '../../../gallery/pipes/asset-status-icon.pipe';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { UnseenActivityService } from '../../../services/unseen-activity.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';

@Component({
  selector: 'app-asset-feedback-hints',
  templateUrl: './asset-feedback-hints.component.html',
  styleUrls: ['./asset-feedback-hints.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgComponent, AssetStatusIconPipe, AssetStatusColorPipe],
})
export class AssetFeedbackHintsComponent {
  private permissionService = inject(PermissionService);
  private unseenActivityService = inject(UnseenActivityService);
  private state = inject(StateService);
  readonly asset = input.required<AssetDto>();
  readonly likes = input.required<number>();
  readonly is_liked = input.required<boolean>();
  readonly status = input.required<AssetStatus>();
  readonly color_label = input.required<AssetColorLabel>();
  readonly gallery = input<GalleryDto>();
  readonly galleryPermissions = input<GalleryDto['permissions']>();
  readonly simpleMode = input<boolean>();
  readonly darkBackground = input<boolean>();

  showUnseenActivityIcon = toSignal(
    toObservable(this.asset).pipe(
      filter(Boolean),
      distinctUntilKeyChanged('id'),
      switchMap(asset =>
        this.unseenActivityService.unseenActivityChange$.pipe(
          map(() => {
            const count = this.state.unseenActivityCountMap.get(asset.id) || 0;
            return count > 0;
          })
        )
      )
    )
  );

  private feedbackOptions = computed(() => {
    if (this.simpleMode()) return allFeedbackOptions;
    const gallery = this.gallery();
    const galleryPermissions = this.galleryPermissions();
    if (!gallery || !galleryPermissions) return;
    return this.permissionService.getAllowedAndEnabledFeedbackOptions(
      gallery,
      galleryPermissions.can_review_if_authenticated
    );
  });

  showFavouriteIcon = computed(() => {
    const options = this.feedbackOptions();
    const likes = this.likes();
    if (!options) return;
    return options.includes('favourite') && !!likes;
  });

  showStatusIcon = computed(() => {
    const options = this.feedbackOptions();
    const status = this.status();
    if (!options) return;
    return options.includes('status') && !!status && status !== 'no_status';
  });

  showColorLabelIcon = computed(() => {
    const options = this.feedbackOptions();
    const colorLabel = this.color_label();
    if (!options) return;
    return options.includes('color_label') && !!colorLabel;
  });

  showCommentIcon = computed(() => {
    const options = this.feedbackOptions();
    const asset = this.asset();
    if (!options) return;
    return options.includes('comment') && !!asset.unresolved_comments_count;
  });

  feedbackBarCount = computed(() => {
    const showFavouriteIcon = this.showFavouriteIcon();
    const showStatusIcon = this.showStatusIcon();
    const showColorLabelIcon = this.showColorLabelIcon();
    const showCommentIcon = this.showCommentIcon();
    return (
      (showFavouriteIcon ? 1 : 0) + (showStatusIcon ? 1 : 0) + (showColorLabelIcon ? 1 : 0) + (showCommentIcon ? 1 : 0)
    );
  });

  showFeedbackBar = computed(() => {
    return this.feedbackBarCount() > 0;
  });
}
